import React, { useEffect, useState, useMemo } from "react";
import "./AddDriver.css";
import ApiManager from "../../ApiManager/ApiManager";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import CustomSelect from "../../components/CustomSelect";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import { useComponentLoading } from "../../utils/useComponentLoading";

const EditDriver = ({ driverId: propDriverId , onlySalaryInfo = false, toggleModal}) => {
  const { driverId: paramDriverId } = useParams();
  const id = propDriverId || paramDriverId;
  const navigate = useNavigate();
  const { 
    loadingStates, 
    loadingMessages, 
    withLoading,
    getMessage,
    isLoading 
  } = useComponentLoading({
    fetchDriver: false,
    updateDriver: false
  });

  const [salaryType, setSalaryType] = useState("");

  const convertTimeToNumber = (inputTime) => {
    if (inputTime) {
      let [time, modifier] = inputTime.split(" ");
      let [hours, minutes] = time.split(":");

      hours = Number(hours);
      minutes = Number(minutes);

      if (modifier?.toLowerCase() === "pm" && hours !== 12) {
        hours += 12;
      } else if (modifier?.toLowerCase() === "am" && hours === 12) {
        hours = 0;
      }

      const formattedTime = `${String(hours).padStart(2, "0")}:${String(
        minutes
      ).padStart(2, "0")}:00`;
      return formattedTime;
    }
    return null;
  };

  const convertDateToString = (inputDate) => {
    if (!inputDate) return null;

    const parts = inputDate.split("-");

    if (parts.length !== 3) return null;

    if (parts[0].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert to "dd-mm-yyyy"
    } else {
      return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert to "yyyy-mm-dd"
    }
  };

  const [driverData, setDriverData] = useState({
    username: "",
    vendorId: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    address: "",
    birthDate: "",
    joinDate: "",
    phone: "",
    licenseNo: "",
    licenseExp: "",
    driverSalaryInfo: {
      salaryType: "",
      salary: 0,
      daily_wage: 0,
      working_start_time: 0,
      working_end_time: 0,
      daily_allowance: 0,
      overtime_per_hour: 0,
      outstation_allowance: 0,
      sunday_allowance: 0,
      night_allowance: 0,
    },
  });

  // Create memoized options for salary type
  const salaryTypeOptions = useMemo(() => [
    { value: "fixed", label: "Fixed" },
    { value: "daily", label: "Daily" }
  ], []);

  // Fetch driver details
  useEffect(() => {
    const fetchDriverDetails = async () => {
      try {
        const query = new URLSearchParams({ driverId: id });
        const response = await ApiManager.getDriver(query);
        if (!response.ok) throw new Error("Error fetching driver details");

        const driverDetails = await response.json();
        setDriverData({
          ...driverDetails,
        });
        setSalaryType(driverDetails.driverSalaryInfo.salaryType);
      } catch (error) {
        console.error("Error fetching driver details:", error);
      }
    };

    withLoading('fetchDriver', fetchDriverDetails, 'Fetching driver details...');
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setDriverData((prevData) => ({
      ...prevData,
      [name]: type === "date" ? convertDateToString(value) : value,
    }));
  };

  const handleSalaryInfoChange = (field, value) => {
    if (field === "salaryType") {
      setSalaryType(value);
    }
    setDriverData((prevData) => ({
      ...prevData,
      driverSalaryInfo: {
        ...prevData.driverSalaryInfo,
        [field]: field === "working_start_time" || field === "working_end_time"
          ? convertTimeToNumber(value)
          : Number(value),
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...driverData,
      driverSalaryInfo: {
        ...driverData.driverSalaryInfo,
        salaryType: salaryType
      }
    };

    try {
      const query = new URLSearchParams({ driverId: id });
      await withLoading('updateDriver', async () => {
        const response = await ApiManager.updateDriver(query, payload);
        if (!response.ok) throw new Error(await response.text());

        if (onlySalaryInfo) {
          toggleModal();
          window.location.reload();
          toast.success('Driver details updated successfully!');
        } else {
          navigate('/login/drivers/all', {
            state: {
              toast: {
                type: 'success',
                message: 'Driver details updated successfully!'
              }
            }
          });
        }
      }, 'Updating driver details...');
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.message || "Failed to update driver details");
    }
  };

  if (loadingStates.fetchDriver) {
    return (
      <div>
        <LoadingSpinner 
          size="large" 
          message={getMessage('fetchDriver') || "Loading driver details..."}
        />
      </div>
    );
  }

  if (onlySalaryInfo) {
    return (
      <div className="addDriver">
        <ToastContainer />
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="formGroup">
              <label>Salary Type *</label>
              <div style={{ marginTop: "var(--spacing-md)" }}>
              <CustomSelect
                options={salaryTypeOptions}
                value={salaryType}
                onChange={(option) => handleSalaryInfoChange("salaryType", option ? option.value : "")}
                placeholder="Select Salary Type"
                required
              />
            </div>
            </div>
          </div>
          <div className="row">
            <div className="formGroup">
              <label>Salary *</label>
              <input
                type="number"
                name="salary"
                value={driverData.driverSalaryInfo.salary}
                onChange={(e) =>
                  handleSalaryInfoChange("salary", e.target.value)
                }
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Daily Wage *</label>
              <input
                type="number"
                name="daily_wage"
                value={driverData.driverSalaryInfo.daily_wage}
                onChange={(e) =>
                  handleSalaryInfoChange("daily_wage", e.target.value)
                }
                required
                className="input"
              />
            </div>
          </div>
          <div className="row">
            <div className="formGroup">
              <label>Daily Allowance *</label>
              <input
                type="number"
                name="daily_allowance"
                value={driverData.driverSalaryInfo.daily_allowance}
                onChange={(e) =>
                  handleSalaryInfoChange("daily_allowance", e.target.value)
                }
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Overtime Per Hour *</label>
              <input
                type="number"
                name="overtime_per_hour"
                value={driverData.driverSalaryInfo.overtime_per_hour}
                onChange={(e) =>
                  handleSalaryInfoChange("overtime_per_hour", e.target.value)
                }
                required
                className="input"
              />
            </div>
          </div>
          <div className="row">
            <div className="formGroup">
              <label>Outstation Allowance *</label>
              <input
                type="number"
                name="outstation_allowance"
                value={driverData.driverSalaryInfo.outstation_allowance}
                onChange={(e) =>
                  handleSalaryInfoChange("outstation_allowance", e.target.value)
                }
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Sunday Allowance *</label>
              <input
                type="number"
                name="sunday_allowance"
                value={driverData.driverSalaryInfo.sunday_allowance}
                onChange={(e) =>
                  handleSalaryInfoChange("sunday_allowance", e.target.value)
                }
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Night Allowance *</label>
              <input
                type="number"
                name="night_allowance"
                value={driverData.driverSalaryInfo.night_allowance}
                onChange={(e) =>
                  handleSalaryInfoChange("night_allowance", e.target.value)
                }
                required
                className="input"
              />
            </div>
          </div>

        <button 
          type="submit" 
          className={`submitButton ${isLoading('updateDriver') ? 'loading' : ''}`}
        >
          {isLoading('updateDriver') ? (
            <LoadingSpinner size="xs" inline={true} message="Updating Driver..." />
          ) : (
            "Submit"
          )}
        </button>
        </form> 
      </div>
    );
  }

  return (
    <div className="addDriver">
      <ToastContainer />
      <div style={{ textAlign: "center" }}>
        <h3>Edit Driver</h3>
      </div>
      <form onSubmit={handleSubmit}>
        <fieldset className="fieldset">
          <legend>Driver Details</legend>
          <div className="row">
            <div className="formGroup">
              <label>Username *</label>
              <input
                type="text"
                name="username"
                value={driverData.username}
                onChange={(e) =>handleInputChange(e)}
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Vendor ID *</label>
              <input
                type="text"
                name="vendorId"
                value={driverData.vendorId}
                onChange={(e) =>handleInputChange(e)}
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>First Name *</label>
              <input
                type="text"
                name="firstName"
                value={driverData.firstName}
                onChange={(e) =>handleInputChange(e)}
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={driverData.lastName}
                onChange={(e) =>handleInputChange(e)}
                required
                className="input"
              />
            </div>
          </div>
          <div className="row">
            <div className="formGroup">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={driverData.email}
                onChange={(e) =>handleInputChange(e)}
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Birth Date *</label>
              <input
                type="date"
                name="birthDate"
                value={convertDateToString(driverData.birthDate) || ""}
                onChange={(e) =>handleInputChange(e)}
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Join Date *</label>
              <input
                type="date"
                name="joinDate"
                value={convertDateToString(driverData.joinDate) || ""}
                onChange={(e) =>handleInputChange(e)}
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Phone *</label>
              <input
                type="text"
                name="phone"
                value={driverData.phone}
                onChange={(e) =>handleInputChange(e)}
                required
                className="input"
              />
            </div>
          </div>
          <div className="formGroup">
            <label>Address *</label>
            <textarea
              name="address"
              value={driverData.address}
              onChange={(e) =>handleInputChange(e)}
              required
              className="input"
            />
          </div>
        </fieldset>
        <fieldset className="fieldset">
          <legend>License Information</legend>
          <div className="row">
            <div className="formGroup">
              <label>License No *</label>
              <input
                type="text"
                name="licenseNo"
                value={driverData.licenseNo}
                onChange={(e) =>handleInputChange(e)}
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>License Expiry *</label>
              <input
                type="date"
                name="licenseExp"
                value={convertDateToString(driverData.licenseExp) || ""}
                onChange={(e) =>handleInputChange(e)}
                required
                className="input"
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="fieldset">
          <legend>Salary Info</legend>
          <div className="row">
            <div className="formGroup">
              <label>Salary Type *</label>
              <div style={{ marginTop: "var(--spacing-md)" }}>
              <CustomSelect
                options={salaryTypeOptions}
                value={salaryType}
                onChange={(option) => handleSalaryInfoChange("salaryType", option ? option.value : "")}
                placeholder="Select Salary Type"
                required
              />
            </div>
            </div>
          </div>
          <div className="row">
            <div className="formGroup">
              <label>Salary *</label>
              <input
                type="number"
                name="salary"
                value={driverData.driverSalaryInfo.salary}
                onChange={(e) =>
                  handleSalaryInfoChange("salary", e.target.value)
                }
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Daily Wage *</label>
              <input
                type="number"
                name="daily_wage"
                value={driverData.driverSalaryInfo.daily_wage}
                onChange={(e) =>
                  handleSalaryInfoChange("daily_wage", e.target.value)
                }
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Working Start Time *</label>
              <input
                type="time"
                name="working_start_time"
                value={driverData.driverSalaryInfo.working_start_time}
                onChange={(e) =>
                  handleSalaryInfoChange("working_start_time", e.target.value)
                }
                required
                className="input"
              />
            </div>
          </div>
          <div className="row">
            <div className="formGroup">
              <label>Working End Time *</label>
              <input
                type="time"
                name="working_end_time"
                value={driverData.driverSalaryInfo.working_end_time}
                onChange={(e) =>
                  handleSalaryInfoChange("working_end_time", e.target.value)
                }
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Daily Allowance *</label>
              <input
                type="number"
                name="daily_allowance"
                value={driverData.driverSalaryInfo.daily_allowance}
                onChange={(e) =>
                  handleSalaryInfoChange("daily_allowance", e.target.value)
                }
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Overtime Per Hour *</label>
              <input
                type="number"
                name="overtime_per_hour"
                value={driverData.driverSalaryInfo.overtime_per_hour}
                onChange={(e) =>
                  handleSalaryInfoChange("overtime_per_hour", e.target.value)
                }
                required
                className="input"
              />
            </div>
          </div>
          <div className="row">
            <div className="formGroup">
              <label>Outstation Allowance *</label>
              <input
                type="number"
                name="outstation_allowance"
                value={driverData.driverSalaryInfo.outstation_allowance}
                onChange={(e) =>
                  handleSalaryInfoChange("outstation_allowance", e.target.value)
                }
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Sunday Allowance *</label>
              <input
                type="number"
                name="sunday_allowance"
                value={driverData.driverSalaryInfo.sunday_allowance}
                onChange={(e) =>
                  handleSalaryInfoChange("sunday_allowance", e.target.value)
                }
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Night Allowance *</label>
              <input
                type="number"
                name="night_allowance"
                value={driverData.driverSalaryInfo.night_allowance}
                onChange={(e) =>
                  handleSalaryInfoChange("night_allowance", e.target.value)
                }
                required
                className="input"
              />
            </div>
          </div>
        </fieldset>

        <button 
          type="submit" 
          className={`submitButton ${isLoading('updateDriver') ? 'loading' : ''}`}
        >
          {isLoading('updateDriver') ? (
            <LoadingSpinner size="xs" inline={true} message="Updating Driver..." />
          ) : (
            "Submit"
          )}
        </button>
      </form>
    </div>
  );
};

export default EditDriver;
